<script setup lang="ts">
import ElUploadImage from '@/components/rc/ElUploadImage.vue'
import type { INewContainer } from '@/types/rc/containers'
import type { IDamageInstance } from '@/types/rc/shipments'
import { Icon } from '@iconify/vue'
import type { FormInstance } from 'element-plus'
import { computed, ref } from 'vue'

// Available containers is for bulk containers creation
// (should be able to link a created damage to a container)
const props = withDefaults(defineProps<{ disabled: boolean; availableContainers?: INewContainer[] }>(), {
  disabled: false,
})

// Basically to handle scroll events on parent
const emit = defineEmits(['add-damages', 'remove-damage'])

const formRef = ref<FormInstance>()

// The core purpose of the component
const damagesList = defineModel<IDamageInstance[]>({ required: true })

// Compute the list of available containers to assign this damage to
// (in case we're using this component on containers bulk-creation)
const containerNumberList = computed(() => {
  if (!props.availableContainers) return
  return props.availableContainers
    .filter((container) => container.number !== '')
    .map((container) => ({
      key: container.number.toString(),
      value: container.number,
    }))
})

// Exposed to parent
const validate = () => (formRef.value ? formRef.value.validate() : Promise.resolve(true))

// Exposed to parent
const reset = () => {
  damagesList.value.splice(0)
  formRef.value?.resetFields()
}

// Exposed to parent
const resetDamagesSelection = () => {
  damagesList.value.forEach((damage: IDamageInstance) => {
    damage.containerNumber = ''
  })
}

const addDamages = () => {
  damagesList.value.push({
    id: Date.now().toString(),
    file_url: '',
    description: '',
  })
  emit('add-damages')
}

const removeSingleDamage = (id: string) => {
  if (props.disabled) return
  const damageInstance = damagesList.value.find((damage) => damage.id === id)

  if (!damageInstance) {
    return
  }

  const damageIndex = damagesList.value.indexOf(damageInstance)
  damagesList.value.splice(damageIndex, 1)

  // Notify the parent this was removed
  emit('remove-damage', id)
}

// Expose and provide an interface for exposed values
export type IDamagesInputFormExposed = typeof exposed
const exposed = { validate, reset, resetDamagesSelection }
defineExpose(exposed)
</script>

<template>
  <el-form
    ref="formRef"
    :disabled="disabled"
    :model="damagesList"
    :scroll-into-view-options="{ behavior: 'smooth', block: 'start' }"
    scroll-to-error
  >
    <div v-for="(damageInstance, index) in damagesList" :key="damageInstance.id" class="damages-creation-form">
      <div class="damages-creation-form__header">
        <div class="damages-creation-form__header__label">Damage #{{ index + 1 }}</div>
        <div class="damages-creation-form__header__icon">
          <Icon
            icon="material-symbols:delete-outline"
            :height="20"
            :width="20"
            :inline="true"
            @click="removeSingleDamage(damageInstance.id)"
          />
        </div>
      </div>
      <div class="damages-creation-form__add-damage">
        <div class="damages-creation-form__add-damage__img-container">
          <el-form-item
            :key="damageInstance.id"
            :prop="`${index}.picture`"
            :rules="[
              {
                required: !damageInstance.file_url,
                message: 'Please upload an image.',
                trigger: ['blur', 'change'],
              },
            ]"
          >
            <ElUploadImage
              v-if="damageInstance.file_url"
              about="Damages"
              :image-url="damageInstance.file_url"
              class="w-full"
            />
            <ElUploadImage v-else v-model="damageInstance.picture" about="Damages" class="w-full" />
          </el-form-item>
        </div>
        <div class="damages-creation-form__add-damage__info-container">
          <el-form-item
            v-if="availableContainers"
            :key="damageInstance.id"
            class="damages-creation-form__add-damage__info-container__input"
            :prop="`${index}.containerNumber`"
            :rules="[
              {
                required: true,
                message: 'Container number is required.',
                trigger: ['blur', 'change'],
              },
            ]"
          >
            <el-select
              v-model="damageInstance.containerNumber"
              class="damages-creation-form__add-damage__info-container__input"
              placeholder="Select container #"
            >
              <el-option
                v-for="container in containerNumberList"
                :key="container.key"
                :label="container.value"
                :value="container.value"
              >
                {{ container.value }}
              </el-option>
            </el-select>
          </el-form-item>
          <el-input
            :key="damageInstance.id"
            v-model="damageInstance.description"
            type="textarea"
            resize="none"
            :rows="availableContainers ? 3 : 6"
            class="damages-creation-form__add-damage__info-container__input"
            placeholder="Enter damage description"
          />
        </div>
      </div>
    </div>
  </el-form>
  <el-link type="primary" :disabled="disabled" @click="addDamages">
    <Icon icon="material-symbols:add" height="20" :inline="true" />
    Add a damage
  </el-link>
</template>

<style lang="scss">
.damages-creation-form {
  .el-textarea__inner {
    height: 90px;
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    &__label {
      font-size: 14px;
      color: var(--text-component-label);
      margin-bottom: var(--spacing-spacing-xs);
    }

    &__icon {
      cursor: pointer;
      display: none;
      color: var(--text-component-label);
    }
  }

  &:hover {
    .damages-creation-form__header__icon {
      display: block;
    }
  }

  &__add-damage {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-spacing-md);

    &__img {
      border-radius: 8px;

      &-container {
        flex: 1;
        max-width: 240px;
      }
    }

    &__actions {
      &-container {
        display: flex;
        align-items: center;
        cursor: pointer;
        color: var(--text-component-label);
      }
    }

    &__info {
      &-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: var(--spacing-spacing-xs);

        &__input {
          background: var(--component-fill-primary);
          border-radius: 8px;

          textarea {
            height: 90px;
          }
        }

        &__upload-img {
          width: 100%;
        }
      }
    }
  }
}
</style>
